import { AutoScroll, PatientDetailsForm, PatientInputTypes } from "@components";
import SiteGroupPanel, {
  SiteGroupPanelRefObject,
} from "@components/SiteGroupPanel/SiteGroupPanel";
import { ListDataCategory, Role } from "@enums";
import {
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
  Button,
} from "@mui/material";
import {
  SiteGroupOut,
  useProtocolApiV1ListDataQuery,
  useWebApiV1DeleteClonedOrderMutation,
  useWebApiV1GetPatientQuery,
  useWebApiV1ListSiteGroupsQuery,
  useWebApiV1OrderQuery,
  useWebApiV1UpdateOrderMutation,
} from "@providers/hop-ord-server/api";
import { skipToken } from "@reduxjs/toolkit/query/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import DeleteCareplanModal from "./components/DeleteCareplanModal/DeleteCareplanModal";
import DeleteSiteGroupModal from "./components/DeleteSiteGroupModal/DeleteSiteGroupModal";
import ProtocolPanel from "./components/ProtocolPanel/ProtocolPanel";
import CareplanContentsDrawer from "./components/CareplanContentsDrawer/CareplanContentsDrawer";
import { useCheckUserRole, useInterval } from "@utils";
import SiteGroupPanelSkeleton from "@components/SiteGroupPanel/SiteGroupPanelSkeleton";
import PatientDetailsFormSkeleton from "@components/PatientDetailsForm/PatientDetailsFormSkeleton";
import WorkflowPageWrapper from "@pages/Layout/WorkflowPageWrapper";
import BottomActionBar from "@pages/Layout/BottomActionBar";
import FormatListBulleted from "@mui/icons-material/FormatListBulleted";
const DRAFT = "draft";

const ContentsHeaderText = ({ text, id }: { text: string; id: string }) => (
  <Typography
    id={id}
    variant="h6"
    sx={{ scrollMarginTop: "18rem" }}
    paddingBottom={2}
  >
    {text}
  </Typography>
);

const CareplanPage = () => {
  const theme = useTheme();
  const { orderId } = useParams();
  const [updateOrder] = useWebApiV1UpdateOrderMutation();
  const [deleteOrder] = useWebApiV1DeleteClonedOrderMutation();
  const navigate = useNavigate();
  const [deleteSiteGroupModalOpen, setDeleteSiteGroupModalOpen] =
    useState(false);
  const [deleteOrderModalOpen, setDeleteOrderModalOpen] = useState(false);
  const [selectedSiteGroup, setSelectedSiteGroup] =
    useState<SiteGroupOut | null>(null);
  const [submitCount, setSubmitCount] = useState(0);
  const siteGroupRefs = useRef<(SiteGroupPanelRefObject | null)[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDataInSync, setIsDataInSync] = useState(false);
  const [patientForm, setPatientForm] =
    useState<UseFormReturn<PatientInputTypes> | null>(null);
  const [siteGroupValidities, setSiteGroupValidities] = useState<{
    [key: number]: boolean;
  }>({});
  const [drawerOpen, setDrawerOpen] = useState(false);

  const isDosimetrist = useCheckUserRole(Role.DOSIMETRIST);
  const careplanPageName = isDosimetrist ? "commandcenter" : "order";

  const handleReportedSiteGroupValidity = useCallback(
    (siteGroupId: number | string, isValid: boolean) => {
      setSiteGroupValidities((prev) => {
        const updated = { ...prev };
        updated[siteGroupId as number] = isValid;
        return updated;
      });
    },
    [],
  );
  const screenIsLarge = useMediaQuery(theme.breakpoints.up("xl"));

  const { data: careplanData, error: careplanError } = useWebApiV1OrderQuery({
    orderId: Number(orderId),
  });

  // Check if we are editing a submitted careplan
  const isEditing = careplanData?.submittedDatetime !== null;

  // Get Patient Data
  const { data: patientData, error: patientError } = useWebApiV1GetPatientQuery(
    careplanData?.patientId !== undefined && careplanData?.patientId !== null
      ? { patientId: careplanData.patientId }
      : skipToken,
  );

  const { data: treatingDepartments } = useProtocolApiV1ListDataQuery({
    categories: [ListDataCategory.TREATING_DEPARTMENT],
    tenantIds: [careplanData?.tenantId ?? -1],
  });

  const handleUpdateOrderField = useCallback(
    // Updates a single field in the order
    (field: string, value: string | number) => {
      updateOrder({
        orderId: Number(orderId),
        orderIn: { ...careplanData, [field]: value },
      })
        .unwrap()
        .then((payload) => {
          if (field === "status" && payload.status === "submitted") {
            // If we're submitting the order as an RO, navigate to the tracker page
            // If submitting as a dosimetrist, navigate to the home page
            // If we're editing the order as a dosimetrist, navigate to the previous page
            const navigation = `/commandcenter/${orderId}`;
            setIsSubmitting(false);
            if (value === "submitted") {
              navigate(navigation);
            }
          }
        });
    },
    [careplanData, orderId, updateOrder, navigate],
  );

  // Automatically set the treating department if there is only one
  useEffect(() => {
    if (treatingDepartments?.length === 1) {
      handleUpdateOrderField(
        "treatingDepartment",
        treatingDepartments[0].value,
      );
    }
  }, [treatingDepartments, handleUpdateOrderField]);

  // Get Site Groups
  const { data: siteGroupData, error: siteGroupError } =
    useWebApiV1ListSiteGroupsQuery({ orderId: Number(orderId) });
  const isReadOnly = careplanData?.status !== DRAFT;

  // Discard Order
  const discardOrder = async () => {
    if (isReadOnly) return;
    const parentId = careplanData?.parentId;
    await deleteOrder({ orderId: Number(orderId) }).then(() => {
      // Using replace prevents the back button from navigating to the deleted order
      if (parentId) {
        // If a dosimetrist go to automation, otherwise go to the order page
        const navigation = isDosimetrist
          ? `/commandcenter/${parentId}`
          : `/order/${parentId}`;
        navigate(navigation);
      } else {
        navigate(`/${careplanPageName}`);
      }
    });
  };

  const submitOrder = async () => {
    if (isReadOnly) return;
    const patientValid = await patientForm?.trigger();
    setSubmitCount(submitCount + 1);

    if (!patientValid) {
      return;
    }
    setIsSubmitting(true);
    setIsDataInSync(false);
  };

  // Keep checking the tables to make sure they are in sync with backend
  useInterval(
    () => {
      const numberOfSiteGroups = siteGroupData?.length || 0;
      const siteGroupInSync = siteGroupRefs.current
        .map((ref, index) => {
          if (index >= numberOfSiteGroups) return true;
          return ref?.trigger();
        })
        .every((valid) => valid);
      setIsDataInSync(siteGroupInSync);
    },
    isSubmitting && !isDataInSync ? 1000 : null,
  );

  // Wait for the data sync to finish before submitting
  useEffect(() => {
    if (isSubmitting && isDataInSync) {
      if (!siteGroupData?.length) {
        setIsSubmitting(false);
        return;
      }

      if (Object.keys(siteGroupValidities).length !== siteGroupData?.length) {
        setIsSubmitting(false);
        return;
      }

      if (Object.values(siteGroupValidities).includes(false)) {
        setIsSubmitting(false);
        return;
      }

      if (!careplanData?.treatingRo) {
        setIsSubmitting(false);
        return;
      }

      handleUpdateOrderField("status", "submitted");
    }
  }, [
    isSubmitting,
    isDataInSync,
    siteGroupData,
    siteGroupValidities,
    handleUpdateOrderField,
    isDosimetrist,
    careplanData?.treatingRo,
  ]);

  // Open Delete Site Group Modal
  const openDeleteSiteGroupModal = (siteGroup: SiteGroupOut) => {
    if (isReadOnly) return;
    setSelectedSiteGroup(siteGroup);
    setDeleteSiteGroupModalOpen(true);
  };

  // show a blank screen, a bad query will trigger the global error alert
  if (careplanError || patientError || siteGroupError) return null;

  return (
    <WorkflowPageWrapper currentPageIndex={0} orderId={Number(orderId)}>
      <Stack
        padding={2}
        paddingTop={3}
        spacing={3}
        maxWidth="1152px"
        margin="0 auto"
        width="100%"
        paddingBottom={6}
      >
        <Typography variant="h5">
          {isEditing ? "Edit Careplan" : "Step 1: Submit Careplan"}
        </Typography>
        {!screenIsLarge && (
          <Stack direction="row" justifyContent="space-between">
            <Button
              variant="outlined"
              startIcon={<FormatListBulleted />}
              onClick={() => {
                setDrawerOpen(true);
              }}
            >
              Contents
            </Button>
          </Stack>
        )}

        {/* Patient Details */}
        {!careplanData || !patientData ? (
          <PatientDetailsFormSkeleton />
        ) : (
          <PatientDetailsForm
            setForm={setPatientForm}
            patientId={careplanData?.patientId ?? -1}
            orderId={Number(orderId)}
            orderData={careplanData}
            patientData={patientData}
            isReadOnly={isReadOnly}
          />
        )}

        {/* Protocol Panel */}
        {careplanData && (
          <Stack>
            <ContentsHeaderText
              text="Planning Protocol"
              id="planning-protocol"
            />
            <ProtocolPanel
              isReadOnly={isReadOnly}
              orderId={Number.parseInt(orderId ?? "")}
              tenantId={careplanData.tenantId}
            />
          </Stack>
        )}

        {/* Site Groups */}
        {!careplanData || !siteGroupData ? (
          <SiteGroupPanelSkeleton />
        ) : (
          siteGroupData?.map((siteGroup, index) => (
            <SiteGroupPanel
              index={index}
              orderId={Number.parseInt(orderId ?? "")}
              tenantId={careplanData.tenantId}
              key={siteGroup.id}
              siteGroup={siteGroup}
              isReadOnly={isReadOnly || isSubmitting} // Disable editing if submitting
              submitCount={submitCount}
              reportValidity={handleReportedSiteGroupValidity}
              onDeleteCallback={openDeleteSiteGroupModal}
              doseUnit={careplanData.doseUnit}
              fractionUnit={careplanData.fractionUnit}
            />
          ))
        )}

        <AutoScroll submitCount={submitCount} />
      </Stack>

      <DeleteSiteGroupModal
        open={deleteSiteGroupModalOpen}
        setOpen={setDeleteSiteGroupModalOpen}
        selectedSiteGroup={selectedSiteGroup}
        orderId={Number(orderId)}
        onSiteGroupDeletedCallback={(siteGroupId: number) => {
          // Remove the site group from the validities array
          setSiteGroupValidities((prev) =>
            Object.fromEntries(
              Object.entries(prev).filter(
                ([k]) => k !== siteGroupId.toString(),
              ),
            ),
          );
        }}
      />

      <DeleteCareplanModal
        open={deleteOrderModalOpen}
        setOpen={setDeleteOrderModalOpen}
        deleteOrder={discardOrder}
        lastSubmittedAt={careplanData?.submittedDatetime || null}
      />

      <CareplanContentsDrawer
        permanent={screenIsLarge}
        addedSiteGroups={siteGroupData ?? []}
        onDeleteSiteGroup={openDeleteSiteGroupModal}
        setOpen={setDrawerOpen}
        open={drawerOpen}
        isLoading={!careplanData}
      />

      <BottomActionBar>
        <Stack
          direction="row"
          justifyContent="space-between"
          padding={2}
          borderTop={`1px solid ${theme.palette.divider}`}
        >
          <Stack direction="row" gap={2}>
            <Button
              variant="outlined"
              onClick={() => setDeleteOrderModalOpen(true)}
            >
              {isEditing ? "Discard Changes" : "Discard Careplan"}
            </Button>
          </Stack>
          <Stack direction="row" gap={2}>
            <Button variant="outlined" onClick={() => navigate(-1)}>
              Save as draft
            </Button>
            <Button onClick={submitOrder} variant="contained">
              Next: Plan Generation
            </Button>
          </Stack>
        </Stack>
      </BottomActionBar>
    </WorkflowPageWrapper>
  );
};

export default CareplanPage;
